import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useManufacturerStore } from './stores/manufacturer-store';
import { ManufacturerList } from './components/organisms/ManufacturerList';
import { ManufacturerForm } from './components/organisms/ManufacturerForm';
import { ManufacturerDeleteDialog } from './components/organisms/ManufacturerDeleteDialog';
import {
  ManufacturerItem,
  CreateManufacturerRequest,
  UpdateManufacturerRequest,
} from './types/manufacturer-types';

export function Manufacturers() {
  const {
    manufacturers,
    isLoading,
    isSubmitting,
    error,
    totalCount,
    availablePlaceholders,
    exampleTemplates,
    finalCostExampleTemplates,
    fetchManufacturers,
    createManufacturer,
    updateManufacturer,
    deleteManufacturer,
    clearError,
  } = useManufacturerStore();

  // UI State
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedManufacturer, setSelectedManufacturer] = useState<ManufacturerItem | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // Fetch manufacturers on mount
  useEffect(() => {
    fetchManufacturers();
  }, [fetchManufacturers]);

  const handleAdd = () => {
    setSelectedManufacturer(null);
    setFormError(null);
    setIsFormOpen(true);
  };

  const handleEdit = (manufacturer: ManufacturerItem) => {
    setSelectedManufacturer(manufacturer);
    setFormError(null);
    setIsFormOpen(true);
  };

  const handleDeleteClick = (manufacturer: ManufacturerItem) => {
    setSelectedManufacturer(manufacturer);
    setIsDeleteDialogOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setSelectedManufacturer(null);
    setFormError(null);
  };

  const handleFormSubmit = async (data: CreateManufacturerRequest | UpdateManufacturerRequest) => {
    try {
      setFormError(null);

      if (selectedManufacturer) {
        // Update
        await updateManufacturer(selectedManufacturer.id, data as UpdateManufacturerRequest);
        toast.success('Manufacturer updated successfully');
      } else {
        // Create
        await createManufacturer(data as CreateManufacturerRequest);
        toast.success('Manufacturer created successfully');
      }

      handleFormClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setFormError(errorMessage);
      // Don't close form on error so user can fix issues
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedManufacturer) return;

    try {
      await deleteManufacturer(selectedManufacturer.id);
      toast.success(`"${selectedManufacturer.manufacturerName}" deleted successfully`);
      setIsDeleteDialogOpen(false);
      setSelectedManufacturer(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete manufacturer';
      toast.error(errorMessage);
    }
  };

  const handleDeleteClose = () => {
    setIsDeleteDialogOpen(false);
    setSelectedManufacturer(null);
  };

  return (
    <div className="p-6 max-w-full mx-auto h-full overflow-hidden">
      <ManufacturerList
        manufacturers={manufacturers}
        isLoading={isLoading}
        error={error}
        totalCount={totalCount}
        onRefresh={fetchManufacturers}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDeleteClick}
        onClearError={clearError}
      />

      <ManufacturerForm
        isOpen={isFormOpen}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
        manufacturer={selectedManufacturer}
        isSubmitting={isSubmitting}
        availablePlaceholders={availablePlaceholders}
        exampleTemplates={exampleTemplates}
        finalCostExampleTemplates={finalCostExampleTemplates}
        error={formError}
      />

      <ManufacturerDeleteDialog
        manufacturer={selectedManufacturer}
        isOpen={isDeleteDialogOpen}
        isDeleting={isSubmitting}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}

export default Manufacturers;
